/**
 * Fabrique des blocs CMS avancé de la categorie "payment" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { CMS_ICON_NAMES } from "@/lib/cms-puck/blocks/icons";
import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { DEFAULT_BUTTON_APPEARANCE, type ButtonAppearance } from "@/lib/cms-puck/blocks/button";
import { ButtonAppearanceField } from "@/components/cms-puck/fields/ButtonAppearanceField";
import { StripePricePickerField } from "@/components/cms-puck/fields/StripePricePickerField";
import { DEFAULT_STRIPE_PAYMENT_TARGET, type StripePaymentTarget } from "@/lib/cms-puck/blocks/pricing-payment";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { PaymentButtonBlock } from "@/components/cms-puck/blocks/button-blocks";
import { ExpertiseOffersBlock } from "@/components/cms-puck/blocks/expertise-blocks";
import { EshopListingBlock } from "@/components/cms-puck/blocks/eshop-blocks";
import { SubscriptionOffersBlock } from "@/components/cms-puck/blocks/subscription-blocks";
import { type RawComponentsConfig } from "../types";

export function buildPaymentBlocks() {
  return {
    PaymentButton: {
      label: cmsNew("cms.new.auto.label.bouton_paiement", "Bouton paiement"),
      fields: {
        label: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.libelle_traduisible",
            "Libellé (traduisible)",
          ),
          contentEditable: true,
        },
        payment: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.produit_stripe",
            "Produit Stripe",
          ),
          render: ({ value, onChange }) => (
            <StripePricePickerField
              value={value as StripePaymentTarget | undefined}
              onChange={onChange}
            />
          ),
        },
        variant: {
          type: "select",
          label: cmsNew("cms.new.auto.label.style_de_base", "Style de base"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.principal", "Principal"),
              value: "primary",
            },
            {
              label: cmsNew("cms.new.auto.label.secondaire", "Secondaire"),
              value: "secondary",
            },
            {
              label: cmsNew("cms.new.auto.label.contour", "Contour"),
              value: "outline",
            },
          ],
        },
        iconName: {
          type: "select",
          label: cmsNew("cms.new.blocks.icon.label", "Icône"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.aucune", "Aucune"),
              value: "",
            },
            ...CMS_ICON_NAMES.map((n) => ({ label: n, value: n })),
          ],
        },
        iconPosition: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.position_de_licone",
            "Position de l'icône",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.avant", "Avant"),
              value: "before",
            },
            {
              label: cmsNew("cms.new.auto.label.apres", "Après"),
              value: "after",
            },
          ],
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        appearance: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.au_survol", "Au survol"),
          render: ({ value, onChange }) => (
            <ButtonAppearanceField
              value={value as ButtonAppearance | undefined}
              onChange={onChange}
              sections={["hover"]}
            />
          ),
        },
      },
      defaultProps: {
        label: cmsNew("cms.new.auto.label.payer", "Payer"),
        payment: { ...DEFAULT_STRIPE_PAYMENT_TARGET },
        variant: "primary",
        align: "left",
        iconName: "",
        iconPosition: "before",
        appearance: DEFAULT_BUTTON_APPEARANCE,
      },
      render: ({
        label,
        payment,
        variant,
        align,
        iconName,
        iconPosition,
        ...rest
      }) => {
        const r = rest as {
          id?: string;
          appearance?: ButtonAppearance;
          style?: BlockStyle;
        };
        return (
          <PaymentButtonBlock
            id={r.id}
            label={label}
            payment={payment as StripePaymentTarget | undefined}
            variant={variant}
            align={align}
            iconName={iconName || null}
            iconPosition={iconPosition}
            appearance={r.appearance}
            style={r.style}
          />
        );
      },
    },
    ExpertiseOffers: {
      label: cmsNew("cms.new.blocks.expertiseOffers.label", "Offres d'expertise"),
      fields: {
        title: {
          type: "text",
          label: cmsNew("cms.new.auto.label.titre", "Titre"),
          contentEditable: true,
        },
      },
      // Titre vide par défaut : un texte d'auteur n'a pas sa place dans une clé.
      defaultProps: { title: "" },
      render: ({ title }) => <ExpertiseOffersBlock title={title} />,
    },
    SubscriptionOffers: {
      label: cmsNew(
        "cms.new.blocks.subscriptionOffers.label",
        "Forfaits d'abonnement",
      ),
      fields: {
        info: {
          type: "custom",
          label: cmsNew("cms.new.blocks.subscriptionOffers.label", "Forfaits d'abonnement"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.subscriptionOffers",
                "Affiche une carte par forfait d'abonnement. Les forfaits se gèrent dans Stripe.",
              )}
            </p>
          ),
        },
      },
      render: () => <SubscriptionOffersBlock />,
    },
    EshopListing: {
      label: cmsNew("cms.new.blocks.eshopListing.label", "Liste de la boutique"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew("cms.new.nav.eshop", "E-shop"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.eshopListing",
                "Affiche les articles en vente et leurs filtres. Les articles se gèrent dans E-shop.",
              )}
            </p>
          ),
        },
      },
      render: () => <EshopListingBlock />,
    },
  } satisfies Pick<
    RawComponentsConfig,
    "PaymentButton" | "ExpertiseOffers" | "SubscriptionOffers" | "EshopListing"
  >;
}
